import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// Load an <img> from a data URL / blob URL for canvas drawing.
const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

/** Given the source image + pixel crop rect, draw the crop into a canvas and
 *  return a JPEG File. */
async function getCroppedFile(src, area, name = "photo.jpg") {
  const img = await loadImage(src);
  const canvas = document.createElement("canvas");
  canvas.width = area.width;
  canvas.height = area.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    img,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    area.width,
    area.height,
  );
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        const file = new File([blob], name, { type: "image/jpeg" });
        resolve(file);
      },
      "image/jpeg",
      0.92,
    );
  });
}

/**
 * PhotoCropDialog — opens with a source image (dataURL or blob URL), lets the
 * user drag / zoom a square crop, and returns the resulting File + preview
 * dataURL via onConfirm.
 */
export default function PhotoCropDialog({ src, open, onCancel, onConfirm }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixelArea, setPixelArea] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_area, areaPx) => {
    setPixelArea(areaPx);
  }, []);

  const confirm = async () => {
    if (!src || !pixelArea) return;
    setBusy(true);
    try {
      const file = await getCroppedFile(src, pixelArea);
      const reader = new FileReader();
      reader.onload = () => {
        onConfirm?.(file, String(reader.result));
      };
      reader.readAsDataURL(file);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? null : onCancel?.())}>
      <DialogContent
        className="border-white/10 bg-[#0b0b0f] p-0 text-white sm:max-w-md"
        data-testid="photo-crop-dialog"
      >
        <DialogHeader className="p-4 pb-2">
          <DialogTitle>Crop photo</DialogTitle>
          <DialogDescription className="text-white/60">
            Drag to reposition, pinch or use the slider to zoom, then save.
          </DialogDescription>
        </DialogHeader>

        <div className="relative h-72 w-full bg-black">
          {src && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              restrictPosition
            />
          )}
        </div>

        <div className="flex items-center gap-3 px-4 pt-3 text-xs text-white/60">
          <span>Zoom</span>
          <input
            type="range"
            min={1}
            max={4}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            data-testid="photo-crop-zoom"
            className="w-full accent-[#1a3fd1]"
          />
        </div>

        <DialogFooter className="gap-2 border-t border-white/5 p-4">
          <Button
            variant="outline"
            onClick={() => onCancel?.()}
            data-testid="photo-crop-cancel"
            className="border-white/20 bg-transparent text-white hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            onClick={confirm}
            disabled={busy || !pixelArea}
            data-testid="photo-crop-confirm"
            className="bg-[#1a3fd1] text-white hover:bg-[#1a3fd1]/85"
          >
            {busy ? "Cropping…" : "Use this crop"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
